/**
 * tenantBootstrap — multi-tenant self-serve bootstrap.
 *
 * Resolves WHICH tenant this admin-panel page is serving and points the
 * shared `config` object at that tenant's API routes + company BEFORE the
 * app renders. This is how ONE admin-panel deployment serves many self-serve
 * tenants (acme.admin.cleanspacesapp.com, beta.admin.cleanspacesapp.com, ...).
 *
 * OPT-IN + BACKWARD COMPATIBLE. Multi-tenant mode is ON only when
 *   REACT_APP_MULTITENANT === 'true'
 * which is set ONLY on the new shared multi-tenant Vercel project. The
 * existing SixSigma / OBI / CCS deployments do NOT set it, so this returns
 * { mode: 'single' } immediately and nothing changes for them — they keep
 * using their build-time REACT_APP_COMPANY_ID / REACT_APP_BASE_URL exactly
 * as before.
 *
 * In multi-tenant mode:
 *   1. Resolve the tenant slug from the subdomain (or ?tenant= for local dev).
 *   2. GET <api>/enterprise/public/tenant-config/<slug>  (public, pre-login).
 *   3. On ready: set config.baseURL = "<api>t/<slug>/" and config.company =
 *      <companyId>. Every existing `${config.baseURL}Rooms/...` call then
 *      transparently hits the tenant-resolved /t/:slug/* routes against the
 *      tenant's ISOLATED database.
 */
import config from './config'

const MULTITENANT = process.env.REACT_APP_MULTITENANT === 'true'

export type TenantBootResult =
    | { mode: 'single' }
    | { mode: 'multi'; status: 'ready'; slug: string; companyId: string; name?: string }
    | { mode: 'multi'; status: 'provisioning' | 'notfound' | 'error'; slug: string; message?: string }

/** First subdomain label of the hostname, or ?tenant= override for local dev. */
function resolveSlug(): string | null {
    const qs = new URLSearchParams(window.location.search)
    const override = qs.get('tenant')
    if (override) return override.toLowerCase().trim()

    const host = window.location.hostname
    if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null
    const parts = host.split('.')
    if (parts.length < 3) return null // need sub.domain.tld
    const sub = parts[0].toLowerCase()
    if (sub === 'www' || sub === 'admin') return null
    return sub
}

export async function bootstrapTenant(): Promise<TenantBootResult> {
    if (!MULTITENANT) return { mode: 'single' }

    const slug = resolveSlug()
    if (!slug) return { mode: 'multi', status: 'notfound', slug: '' }

    // In multi-tenant mode REACT_APP_BASE_URL is the API ORIGIN (with trailing /).
    const apiHost = (config as any).baseURL as string
    try {
        const url = `${apiHost}enterprise/public/tenant-config/${encodeURIComponent(slug)}`
        const resp = await fetch(url)
        if (resp.status === 404) return { mode: 'multi', status: 'notfound', slug }
        if (!resp.ok) return { mode: 'multi', status: 'error', slug, message: `HTTP ${resp.status}` }
        const data = await resp.json()
        if (!data || !data.ready || !data.companyId) {
            return { mode: 'multi', status: 'provisioning', slug }
        }
        // Point the whole app at this tenant's isolated-DB routes + company.
        ;(config as any).company = data.companyId
        ;(config as any).baseURL = `${apiHost}t/${slug}/`
        return { mode: 'multi', status: 'ready', slug, companyId: data.companyId, name: data.name }
    } catch (e: any) {
        return { mode: 'multi', status: 'error', slug, message: e?.message || 'network error' }
    }
}
